import React from 'react';
import { Box, IconButton, Tooltip, useTheme } from '@mui/material';
import {
  FormatBold as FormatBoldIcon,
  FormatClear as FormatClearIcon,
  Code as CodeIcon
} from '@mui/icons-material';
import Editor from '@monaco-editor/react';

type Props = {
  value: string;
  disabled?: boolean;
  modified?: boolean;
  highlightBg?: string;
  editBorder?: string;
  onChange: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
};

export const RichTextInspectorField: React.FC<Props> = ({
  value,
  disabled = false,
  modified = false,
  highlightBg,
  editBorder,
  onChange,
  onFocus,
  onBlur
}) => {
  const muiTheme = useTheme();
  const ref = React.useRef<HTMLDivElement>(null);
  const lastHtml = React.useRef(value);
  const [viewSource, setViewSource] = React.useState(false);
  const skipNextChange = React.useRef(false);

  // Stable refs so onMount closure never goes stale when parent re-renders
  const onFocusRef = React.useRef(onFocus);
  const onBlurRef = React.useRef(onBlur);
  React.useLayoutEffect(() => { onFocusRef.current = onFocus; }, [onFocus]);
  React.useLayoutEffect(() => { onBlurRef.current = onBlur; }, [onBlur]);

  // Also depends on viewSource: when switching back to visual, the contenteditable
  // remounts fresh (empty innerHTML) but value may not have changed, so without
  // viewSource in deps the effect wouldn't re-run and the editor would stay blank.
  React.useLayoutEffect(() => {
    if (!ref.current) return;
    if (ref.current.innerHTML !== value) {
      ref.current.innerHTML = value;
      // Read back the browser-normalized version so lastHtml never drifts from
      // what the DOM actually contains. Without this, the flush on mode-switch
      // compares raw prop (e.g. "<br/>") vs normalized DOM ("<br>") and fires
      // a false onChange, marking the field modified before the user types.
      lastHtml.current = ref.current.innerHTML;
    }
  }, [value, viewSource]);

  const handleInput = React.useCallback(() => {
    const html = ref.current?.innerHTML ?? '';
    if (html === lastHtml.current) return;
    lastHtml.current = html;
    onChange(html);
  }, [onChange]);

  // If the user has no text selected (collapsed cursor or no selection inside the field),
  // select all field content so bold/clear-format applies to the whole value.
  const ensureSelection = React.useCallback(() => {
    if (!ref.current) return;
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || !ref.current.contains(sel.anchorNode)) {
      const range = document.createRange();
      range.selectNodeContents(ref.current);
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
  }, []);

  const execBold = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    ensureSelection();
    document.execCommand('bold');
    handleInput();
  }, [ensureSelection, handleInput]);

  const execClearFormat = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    ensureSelection();
    document.execCommand('removeFormat');
    handleInput();
  }, [ensureSelection, handleInput]);

  const handleMonacoChange = React.useCallback((val: string | undefined) => {
    if (skipNextChange.current) {
      skipNextChange.current = false;
      return;
    }
    onChange(val ?? '');
  }, [onChange]);

  const handleMonacoMount = React.useCallback((editor: Parameters<NonNullable<React.ComponentProps<typeof Editor>['onMount']>>[0]) => {
    editor.onDidFocusEditorWidget(() => onFocusRef.current?.());
    editor.onDidBlurEditorWidget(() => onBlurRef.current?.());
    skipNextChange.current = true;
    const p = editor.getAction('editor.action.formatDocument')?.run();
    if (p) {
      p.then(
        () => setTimeout(() => { skipNextChange.current = false; }, 0),
        () => { skipNextChange.current = false; }, // cancelled/no formatter — reset immediately
      );
    } else {
      skipNextChange.current = false;
    }
  }, []);

  const monacoOptions = React.useMemo(() => ({
    readOnly: disabled,
    minimap: { enabled: false },
    lineNumbers: 'off' as const,
    wordWrap: 'on' as const,
    scrollBeyondLastLine: false,
    fontSize: 12,
    automaticLayout: true,
    overviewRulerLanes: 0,
    hideCursorInOverviewRuler: true,
    folding: false,
    lineDecorationsWidth: 4,
    lineNumbersMinChars: 0,
    glyphMargin: false,
    renderLineHighlight: 'none' as const,
    hover: { enabled: false },
    padding: { top: 8, bottom: 8 },
    scrollbar: { vertical: 'auto' as const, horizontal: 'auto' as const, alwaysConsumeMouseWheel: false },
  }), [disabled]);

  const borderColor = modified ? editBorder || 'warning.main' : 'divider';
  const borderWidth = modified ? '2px' : '1px';

  if (viewSource) {
    return (
      <Box
        sx={{
          width: '100%',
          border: '1px solid',
          borderColor,
          borderWidth,
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: modified ? highlightBg : 'background.paper',
          ...(disabled && { bgcolor: 'action.disabledBackground' })
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            px: 0.5,
            py: 0.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'action.hover'
          }}
        >
          <Tooltip title="Preview HTML">
            <span>
              <IconButton
                size="small"
                disabled={disabled}
                onClick={() => setViewSource(false)}
                sx={{ p: 0.25, color: 'primary.main' }}
              >
                <CodeIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Editor
          height="180px"
          defaultLanguage="html"
          value={value}
          theme={muiTheme.palette.mode === 'dark' ? 'vs-dark' : 'vs'}
          loading={null}
          onChange={handleMonacoChange}
          onMount={handleMonacoMount}
          options={monacoOptions}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        border: '1px solid',
        borderColor,
        borderWidth,
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: modified ? highlightBg : 'background.paper',
        ...(disabled && { bgcolor: 'action.disabledBackground', cursor: 'not-allowed' })
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 0.5,
          py: 0.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'action.hover'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flex: 1 }}>
          <Tooltip title="Bold">
            <span>
              <IconButton
                size="small"
                disabled={disabled}
                onMouseDown={execBold}
                sx={{ color: 'text.secondary' }}
              >
                <FormatBoldIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Clear formatting">
            <span>
              <IconButton
                size="small"
                disabled={disabled}
                onMouseDown={execClearFormat}
                sx={{ color: 'text.secondary' }}
              >
                <FormatClearIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Tooltip title="View HTML">
          <span>
            <IconButton
              size="small"
              disabled={disabled}
              onClick={() => {
                // Flush any innerHTML not yet captured by onInput before switching
                if (ref.current) {
                  const html = ref.current.innerHTML;
                  if (html !== lastHtml.current) {
                    lastHtml.current = html;
                    onChange(html);
                  }
                }
                setViewSource(true);
              }}
              sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: 'primary.main' } }}
            >
              <CodeIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <Box
        ref={ref}
        component="div"
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={handleInput}
        onFocus={onFocus}
        onBlur={onBlur}
        sx={{
          px: 1.5,
          py: 1,
          minHeight: 72,
          outline: 'none',
          fontSize: '0.875rem',
          lineHeight: 1.5,
          wordBreak: 'break-word',
          cursor: disabled ? 'not-allowed' : 'text',
          '& p': { my: 0 },
          '& p + p': { mt: 1 }
        }}
      />
    </Box>
  );
};

export default RichTextInspectorField;
